<template>
  <div class="menu_schedules--container"
       :class="{'menu_schedules--container-active': isActive,'menu_schedules--container-fixed': isFixed}">
    <a class="menu_schedules--handle visual_menu--glass_effect textual_menu--glass_effect material-icons"
       href="#" :title="handleTitle" @click.prevent="isActive = !isActive">
      {{ handleIcon }}
    </a>

    <div class="menu_schedules--content">
      <div class="menu_schedules--header">
        <h2>Schedules</h2>
      </div>

      <div class="menu_schedules--body">
        <p v-if="!normalizedSchedules.length">No schedules configured for this menu.</p>
        <schedule-display v-for="schedule in normalizedSchedules" :key="schedule.schedule_id"
                          :normalized-schedule="schedule"
                          :class="{'menu_schedules--schedule-active': -1 !== availabilityActiveSchedules.indexOf(schedule.schedule_id)}"></schedule-display>
      </div>

      <div v-if="normalizedSchedules.length" class="menu_schedules--footer">
        <p v-show="availabilityDateTimeMessage.length" v-html="availabilityDateTimeMessage"></p>
        <text-input label="Check Menu Availability" type="datetime-local" v-model="availabilityDateTime"></text-input>
        <material-button label="Check" @click.native="checkMenuAvailability"></material-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from 'vue'
import {MenuScheduleNormalized} from "../defs";
import TextInput from "../../material-form/text-input.vue";
import MaterialButton from "../../material-form/MaterialButton.vue";
import {getDateAsUTC, isNormalizedScheduleActive} from "../../../helpers/menu-sections";
import ScheduleDisplay from "./ScheduleDisplay.vue";

export default defineComponent({
  name: "MenuSchedules",
  components: {ScheduleDisplay, MaterialButton, TextInput},
  props: {
    normalizedSchedules: {
      type: Array as PropType<MenuScheduleNormalized[]>,
      required: true,
    },
    isFixed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isActive: false,
      availabilityDateTime: '',
      availabilityDateTimeMessage: '',
      availabilityActiveSchedules: [] as number[],
    };
  },
  watch: {
    availabilityDateTime() {
      this.availabilityDateTimeMessage = '';
      this.availabilityActiveSchedules = [];
    },
  },
  computed: {
    handleIcon(): string {
      return this.isActive ? 'close' : 'schedule';
    },
    handleTitle(): string {
      return this.isActive ? 'Close Schedules' : 'Open Schedules';
    },
  },
  methods: {
    checkMenuAvailability() {
      if (this.availabilityDateTime.length) {
        if (!this.normalizedSchedules.length) {
          this.availabilityDateTimeMessage = '<span class="menu_schedules--message menu_schedules--message-success"><span class="material-icons">check</span>Menu is active for given date/time.</span>';
        } else {
          const menuTypesActive = [];
          const currentDate = new Date(this.availabilityDateTime);
          const currentDateTimeUtc = getDateAsUTC(currentDate).getTime();
          const currentDayOfWeek = currentDate.getDay();
          const currentTimeOfDay = currentDate.getHours() * 100 + currentDate.getMinutes();
          for (let i = 0, l = this.normalizedSchedules.length; i < l; i++) {
            if (isNormalizedScheduleActive(this.normalizedSchedules[i], currentDateTimeUtc, currentDayOfWeek, currentTimeOfDay)) {
              if (this.normalizedSchedules[i].schedule_is_textual && -1 === menuTypesActive.indexOf('Textual (Outlet Page)')) {
                menuTypesActive.push('Textual (Outlet Page)');
              }
              if (this.normalizedSchedules[i].schedule_is_visual && -1 === menuTypesActive.indexOf('Visual (iPad)')) {
                menuTypesActive.push('Visual (iPad)');
              }
              this.availabilityActiveSchedules.push(this.normalizedSchedules[i].schedule_id);
            }
          }
          if (!menuTypesActive.length) {
            this.availabilityDateTimeMessage = '<span class="menu_schedules--message menu_schedules--message-error"><span class="material-icons">check</span>Menu is not active for given date/time.</span>';
          } else {
            this.availabilityDateTimeMessage = '<span class="menu_schedules--message menu_schedules--message-success"><span class="material-icons">check</span>Menu is visible for given date/time for: ' + menuTypesActive.join(', ') + '</span>';
          }
        }
      }
    }
  },
})
</script>

<style scoped>
.menu_schedules--container {
  --footer-max-height: 10em;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1rem;
}

.menu_schedules--container-fixed {
  position: fixed;
  top: 0;
  left: auto;
  right: 0;
  height: 100%;
  width: 400px;
  box-shadow: -0.25em 0 0.5em rgba(0, 0, 0, 0.35);
  border-radius: initial;
  transform: translateX(100%);
}

.menu_schedules--container-active {
  transform: translateX(-50%);
}

.menu_schedules--container-fixed.menu_schedules--container-active {
  transform: none;
}

.menu_schedules--handle {
  position: absolute;
  right: 100%;
  min-width: 44px;
  min-height: 44px;
  font-size: 3em;
  text-decoration: none;
  border-radius: 0.25em;
}

.menu_schedules--container-fixed .menu_schedules--handle {
  top: 2em;
}

.menu_schedules--content {
  background-color: #FFF;
  width: 400px;
  padding: 1em 1em var(--footer-max-height) 1em;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.54);
  border-radius: 0.25em;
  min-height: 100%;
}

.menu_schedules--content {
  opacity: 0;
  visibility: hidden;
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), visibility 1ms linear 150ms;
}

.menu_schedules--container-active .menu_schedules--content {
  opacity: 1;
  visibility: visible;
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.menu_schedules--footer {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  padding: 1em;
  grid-gap: 1em;
  max-height: var(--footer-max-height);
  border-top: 1px solid rgba(0, 0, 0, 0.54);
  display: grid;
  grid-template-columns: 2fr 1fr;
}

.menu_schedules--footer p {
  grid-column: 1 / -1;
}

.menu_schedules--container:deep(.menu_schedules--message-success) {
  color: green;
}

.menu_schedules--container:deep(.menu_schedules--message-error) {
  color: red;
}
</style>